import React, { useRef, useState } from 'react';
import Button from '../Button';
import { Menu, MenuItem } from '../Menu';
import ResourcePicker from './ResourcePicker';
import { FileGlyph } from './Breadcrumb';
import { useFiles } from '../../data/useFiles';
import useResourceActions from '../../hooks/useResourceActions';
import useUploads from '../../hooks/useUploads';
import UploadProgressList from '../UploadProgress';
import { linkedResources, resourceMessage } from '../../domain/resources';
import { fileLinkMessage } from '../../domain/fileLinks';
import { ACCEPTED_FILE_TYPES } from '../../lib/fileLimits';
import { useDatePattern } from '../../context/LocaleContext';
import s from './resources.module.css';

// Linked Online Resources, in the lesson drawer's Lesson Details tab (#424).
//
// MMS's affordance: the section's title, and a split button + Add File ▾ with
// two entries — Existing Online Resources (the "Link an Online Resource"
// picker) and Add New Online Resources. What the second does in MMS is
// unrecorded; here it uploads the chosen file into the library's Home and
// links it, which keeps "a file is in the library before a lesson can link it"
// true.
//
// Linking and unlinking change the drawer's form only; its Save writes them
// onto the attendance record with the notes (`resourceIds`). An upload is the
// exception, because the bytes have to go somewhere: the file lands in the
// library at once, and stays there if the drawer is then cancelled.
//
// A linked file that has since gone is still listed, with the core's sentence
// for it — deleted from the library, or emptied by the lapse sweep — and can be
// taken off (`linked_resources` in core/src/resources.rs).
export default function LinkedResources({
  ids = [], onLink, onUnlink, title = null, noneText = null, tooManyText = null,
}) {
  const { files, uploadFile, linkFor } = useFiles();
  const actions = useResourceActions(linkFor);
  const [menuOpen, setMenuOpen] = useState(false);
  const [picking, setPicking] = useState(false);
  const inputRef = useRef(null);
  const datePattern = useDatePattern();

  const linked = linkedResources(ids, files, datePattern);

  // Into Home, with each file's progress and refusal as the library page shows
  // them (#577); what landed is linked, which the list above then shows.
  const uploads = useUploads(uploadFile, { onUploaded: (rows) => onLink(rows.map((r) => r.id)) });
  const addNew = (list) => uploads.add(list, { folderId: null });

  return (
    <div className={s.section}>
      <div className={s.sectionTitle}>{title || resourceMessage('linked_title')}</div>

      {linked.length ? (
        <div className={s.linkedList}>
          {linked.map((l) => (
            <div className={s.linked} key={l.id}>
              <FileGlyph />
              {l.state === 'live' ? (
                <>
                  <span className={s.linkedName}>{l.name}</span>
                  <button type="button" className={s.linkAction} onClick={() => actions.open(l.path)}>Open</button>
                  <button type="button" className={s.linkAction} onClick={() => actions.copy(l.path)}>{resourceMessage('copy_link')}</button>
                </>
              ) : (
                <span className={s.linkedGone}>
                  {l.name ? <>{l.name} — </> : null}
                  {l.message}
                </span>
              )}
              <button
                type="button"
                className={s.linkMuted}
                aria-label={`Unlink ${l.name || 'this file'}`}
                onClick={() => onUnlink(l.id)}
              >
                {resourceMessage('unlink')}
              </button>
            </div>
          ))}
        </div>
      ) : (
        <p className={s.note}>{noneText || resourceMessage('none_linked')}</p>
      )}

      <div>
        <Menu
          open={menuOpen}
          onClose={() => setMenuOpen(false)}
          align="left"
          trigger={(
            <Button variant="secondary" onClick={() => setMenuOpen((v) => !v)}>
              + {resourceMessage('add_file')} ▾
            </Button>
          )}
        >
          <MenuItem onClick={() => { setMenuOpen(false); setPicking(true); }}>
            {resourceMessage('add_existing')}
          </MenuItem>
          <MenuItem icon="upload" onClick={() => { setMenuOpen(false); inputRef.current?.click(); }}>
            {resourceMessage('add_new')}
          </MenuItem>
        </Menu>
      </div>

      <UploadProgressList items={uploads.items} onRetry={uploads.retry} onCancel={uploads.cancel} onDismiss={uploads.dismiss} />
      {actions.notice ? (
        <p className={actions.notice.kind === 'error' ? s.error : s.status} role="status">{actions.notice.text}</p>
      ) : null}
      {/* What a family is sent is a link each, minted when the notes go out
          and good for thirty days from then; the email says so too (#575). */}
      {linked.some((l) => l.state === 'live') ? <p className={s.note}>{fileLinkMessage('app_note')}</p> : null}

      <input
        ref={inputRef}
        type="file"
        multiple
        accept={ACCEPTED_FILE_TYPES}
        style={{ display: 'none' }}
        onChange={(e) => {
          const picked = Array.from(e.target.files || []);
          e.target.value = '';
          addNew(picked);
        }}
      />

      <ResourcePicker
        open={picking}
        current={ids}
        onLink={onLink}
        onClose={() => setPicking(false)}
        tooManyText={tooManyText}
      />
    </div>
  );
}
